"use client";
import { Button } from "@/components/ui/button";
import { EMBEDDED_IN_APP_COOKIE } from "@/lib/embeddedInAppCookie";
import { cn } from "@/lib/utils";
import { Check, Share2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

interface ShareProps {
  className?: string;
  title: string;
  variant?: "secondary" | "default";
}

const CONFIRMATION_MS = 2000;

function shareableUrl(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete(EMBEDDED_IN_APP_COOKIE);
  // A recipient did not come from a list, so the link must not say they did.
  url.searchParams.delete("ref");
  return url.toString();
}

export default function Share({
  title,
  variant = "secondary",
  className,
}: ShareProps) {
  const t = useTranslations();
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    [],
  );

  function flash(next: "copied" | "failed") {
    setStatus(next);
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setStatus("idle"), CONFIRMATION_MS);
  }

  async function onShareClick() {
    const url = shareableUrl();

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        if ((error as Error)?.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      flash("copied");
    } catch {
      flash("failed");
    }
  }

  // Keys stay literal so the i18n usage check can see them statically.
  function statusLabel(): string {
    if (status === "copied") return t("share.copied");
    if (status === "failed") return t("share.copyFailed");
    return title;
  }

  return (
    <Button
      variant={variant}
      className={cn(
        "text-sm font-medium py-2 px-4 rounded-full gap-2 h-12",
        className,
      )}
      onClick={onShareClick}
    >
      {status === "copied" ? (
        <Check className="h-5 w-5" aria-hidden />
      ) : (
        <Share2 className="h-5 w-5" aria-hidden />
      )}
      {/* aria-live on the label itself, so the outcome is announced without
          duplicating the text into a second sr-only node — that would make the
          button's accessible name read "Link copied Link copied". */}
      <span aria-live="polite">{statusLabel()}</span>
    </Button>
  );
}
